import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash2, Share2, ListMusic, Globe, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

export default function Playlists() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [publics, setPublics] = useState([]);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [tab, setTab] = useState('mine');

  const load = async () => {
    const [mine, pubs] = await Promise.all([
      api.get('/playlists'), api.get('/playlists/public'),
    ]);
    setPlaylists(mine.data);
    setPublics(pubs.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post('/playlists', { name, description: desc, is_public: isPublic, song_ids: [] });
      setCreating(false); setName(''); setDesc(''); setIsPublic(false);
      load();
      toast.success('Playlist créée');
    } catch (e) {
      setCreating(false); setName(''); setDesc(''); setIsPublic(false);
      if (e?.response?.status === 402) {
        const msg = e.response.data?.detail?.message || 'Limite atteinte';
        toast.error(msg, { action: { label: 'Passer Pro', onClick: () => navigate('/pro') } });
      } else {
        toast.error('Erreur');
      }
    }
  };

  const remove = async (id) => {
    if (!confirm('Supprimer cette playlist ?')) return;
    await api.delete(`/playlists/${id}`);
    load();
  };

  const share = (id) => {
    const url = `${window.location.origin}/playlist/${id}`;
    navigator.clipboard?.writeText(url);
    toast.success('Lien copié dans le presse-papier');
  };

  const list = tab === 'mine' ? playlists : publics;
  const isPro = user?.plan?.is_pro;

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-5xl">
        <div className="mb-8 rise flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline text-orange-400 mb-2">Playlists</div>
            <h1 className="font-display text-5xl tracking-tight">Tes parcours de pratique</h1>
          </div>
          <button data-testid="new-playlist-btn" onClick={() => setCreating(true)} className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} /> Nouvelle playlist
          </button>
        </div>

        {!isPro && (
          <div className="mb-6 glass rounded-xl p-4 flex items-center justify-between text-sm">
            <span className="text-stone-300">Plan Free · 2 playlists max. Passe Pro pour un nombre illimité.</span>
            <button data-testid="pro-cta-playlists" onClick={() => navigate('/pro')} className="btn-ghost text-xs">Découvrir Pro</button>
          </div>
        )}

        <div className="flex gap-2 mb-6 border-b border-stone-800">
          {[
            { k: 'mine', l: `Mes playlists (${playlists.length})` },
            { k: 'public', l: `Communauté (${publics.length})` },
          ].map(t => (
            <button
              key={t.k}
              data-testid={`playlist-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                tab === t.k ? 'border-orange-600 text-stone-100' : 'border-transparent text-stone-400 hover:text-stone-200'
              }`}
            >{t.l}</button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="text-stone-500 py-16 text-center font-display text-2xl">
            {tab === 'mine' ? 'Aucune playlist. Crée-en une !' : 'Aucune playlist publique pour le moment.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {list.map((p, i) => (
              <div key={p.id} data-testid={`playlist-card-${i}`} className="card-stone p-5 rise">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`/playlist/${p.id}`)}>
                    <div className="overline text-stone-500 flex items-center gap-1">
                      {p.is_public ? <Globe size={12} /> : <Lock size={12} />} {p.is_public ? 'public' : 'privé'} · par {p.user_name || 'toi'}
                    </div>
                    <div className="font-display text-2xl mt-1 truncate">{p.name}</div>
                    <div className="text-stone-400 text-sm mt-1 line-clamp-2">{p.description}</div>
                    <div className="mt-3 text-xs text-stone-500 flex items-center gap-2">
                      <ListMusic size={14} /> {p.song_ids?.length || 0} chansons
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 ml-3">
                    <button data-testid={`playlist-share-${i}`} onClick={() => share(p.id)} className="text-stone-400 hover:text-orange-400 p-2"><Share2 size={16} /></button>
                    {tab === 'mine' && (
                      <button data-testid={`playlist-delete-${i}`} onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="glass rounded-2xl max-w-lg w-full p-8" onClick={e => e.stopPropagation()}>
            <div className="overline text-orange-400 mb-1">Nouvelle</div>
            <h2 className="font-display text-3xl mb-6">Créer une playlist</h2>
            <label className="block mb-3">
              <div className="overline text-stone-400 mb-1">Nom</div>
              <input data-testid="playlist-name-input" value={name} onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-stone-900 border border-stone-800 text-stone-100 focus:outline-none focus:border-orange-800" />
            </label>
            <label className="block mb-3">
              <div className="overline text-stone-400 mb-1">Description</div>
              <textarea data-testid="playlist-desc-input" value={desc} onChange={e => setDesc(e.target.value)} rows={3}
                className="w-full px-3 py-2 rounded-lg bg-stone-900 border border-stone-800 text-stone-100 focus:outline-none focus:border-orange-800" />
            </label>
            <label className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer mb-6">
              <input type="checkbox" data-testid="playlist-public-check" checked={isPublic} onChange={e => setIsPublic(e.target.checked)}
                className="accent-orange-600 w-4 h-4" />
              Rendre publique (visible dans la communauté)
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="btn-ghost">Annuler</button>
              <button data-testid="playlist-create-submit" onClick={create} className="btn-primary">Créer</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
